import { useState, useEffect } from "react";
import {
  updateExpensePool,
  createExpensePool,
  deleteExpensePool,
  autoProject,
  fetchDeal,
  type ExpensePool,
  type Deal,
} from "../api/client";
import { fmtCurrency } from "../utils/format";
import { PctInput } from "./ReimbursementEditor";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Save,
  CheckCircle,
} from "lucide-react";

const STANDARD_POOL_IDS = new Set(["pool_cam", "pool_tax", "pool_insurance"]);
const RECOVERY_METHODS = ["nnn", "base_year_stop", "expense_cap", "fixed_psf", "none"];
const METHOD_LABELS: Record<string, string> = {
  nnn: "NNN (Pro-Rata)",
  base_year_stop: "Base Year Stop",
  expense_cap: "Expense Cap",
  fixed_psf: "Fixed $/SF",
  none: "None",
};
const CATEGORY_VARIABLE_DEFAULTS: Record<string, boolean> = {
  janitorial: true, utilities: true, repairs_and_maintenance: false,
  landscaping: false, management_fee: false, administrative: false,
};
const CATEGORY_CONTROLLABLE_DEFAULTS: Record<string, boolean> = {
  janitorial: true, utilities: false, repairs_and_maintenance: true,
  landscaping: true, management_fee: true, administrative: true,
};

interface Props {
  deal: Deal;
  dealId: string;
  onDealUpdate?: (deal: Deal) => void;
}

export default function ExpensePoolsView({ deal, dealId, onDealUpdate }: Props) {
  const propData = deal.property_data as unknown as Record<string, unknown>;
  const [pools, setPools] = useState<ExpensePool[]>(
    (propData?.expense_pools as ExpensePool[]) || []
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    const updated = (propData?.expense_pools as ExpensePool[]) || [];
    if (updated.length > 0) setPools(updated);
  }, [deal]);

  const handleSave = async (pool: ExpensePool) => {
    setSaving(pool.pool_id);
    try {
      const updated = await updateExpensePool(dealId, pool.pool_id, pool);
      setPools((prev) => prev.map((p) => (p.pool_id === pool.pool_id ? updated : p)));
      // Trigger re-projection so cash flow reflects pool changes
      await autoProject(dealId).catch(() => {});
      const refreshed = await fetchDeal(dealId).catch(() => null);
      if (refreshed && onDealUpdate) onDealUpdate(refreshed);
      setSaved(pool.pool_id);
      setTimeout(() => setSaved(null), 1500);
    } catch {
      /* handled by toast in parent */
    } finally {
      setSaving(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const created = await createExpensePool(dealId, { pool_name: newName.trim() });
      setPools((prev) => [...prev, created]);
      setNewName("");
    } catch {
      /* handled by toast */
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (poolId: string) => {
    try {
      await deleteExpensePool(dealId, poolId);
      setPools((prev) => prev.filter((p) => p.pool_id !== poolId));
    } catch {
      /* handled by toast */
    }
  };

  const updatePool = (poolId: string, key: string, value: unknown) => {
    setPools((prev) =>
      prev.map((p) =>
        p.pool_id === poolId ? { ...p, [key]: value } : p
      )
    );
  };

  const toggleItem = (poolId: string, idx: number) => {
    setPools((prev) =>
      prev.map((p) => {
        if (p.pool_id !== poolId) return p;
        const items = [...p.items];
        items[idx] = { ...items[idx], included: !items[idx].included };
        const total = items.filter((i) => i.included).reduce((s, i) => s + i.current_year_amount, 0);
        return { ...p, items, total_amount: total };
      })
    );
  };

  const toggleItemFlag = (poolId: string, idx: number, flag: string) => {
    setPools((prev) =>
      prev.map((p) => {
        if (p.pool_id !== poolId) return p;
        const items = [...p.items];
        const current = (items[idx] as Record<string, unknown>)[flag] as boolean ?? false;
        items[idx] = { ...items[idx], [flag]: !current } as typeof items[number];
        return { ...p, items };
      })
    );
  };

  if (pools.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-grove-300 p-8 text-center">
        <p className="text-bark-400 text-sm">No expense pools available. Upload an operating statement to generate pools.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pools.map((pool) => {
        const isExpanded = expandedId === pool.pool_id;
        const isStandard = STANDARD_POOL_IDS.has(pool.pool_id);
        const typeBadge = pool.pool_type === "cam" ? "bg-forest-50 text-forest-700" :
          pool.pool_type === "tax" ? "bg-honey-50 text-honey-700" :
          pool.pool_type === "insurance" ? "bg-blue-50 text-blue-700" :
          "bg-grove-100 text-bark-500";

        return (
          <div key={pool.pool_id} className="bg-white rounded-xl border border-grove-300 overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-grove-50 transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : pool.pool_id)}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="w-4 h-4 text-bark-400" /> : <ChevronRight className="w-4 h-4 text-bark-400" />}
                <span className="font-display text-base font-semibold text-bark-800">{pool.pool_name}</span>
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${typeBadge}`}>{pool.pool_type}</span>
              </div>
              <span className="text-sm font-semibold text-bark-700 tabular-nums">{fmtCurrency(pool.total_amount)}</span>
            </button>

            {isExpanded && (
              <div className="border-t border-grove-200 px-6 py-5 space-y-5">
                {/* Line items */}
                {pool.items.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-bark-400 tracking-wider mb-2">Line Items</h4>
                    <div className="rounded-lg border border-grove-200 overflow-hidden">
                      <table className="w-full text-xs data-table">
                        <thead>
                          <tr>
                            <th className="w-8"></th>
                            <th>Name</th>
                            <th className="num">Base Year</th>
                            <th className="num">Current Year</th>
                            <th className="text-center w-10" title="Controllable">Ctrl</th>
                            <th className="text-center w-10" title="Variable">Var</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pool.items.map((item, idx) => (
                            <tr key={idx} className={item.included ? "" : "opacity-50"}>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={item.included}
                                  onChange={() => toggleItem(pool.pool_id, idx)}
                                  className="w-3.5 h-3.5"
                                />
                              </td>
                              <td>{item.line_item_name}</td>
                              <td className="num">{fmtCurrency(item.base_year_amount)}</td>
                              <td className="num">{fmtCurrency(item.current_year_amount)}</td>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={item.is_controllable ?? CATEGORY_CONTROLLABLE_DEFAULTS[item.category] ?? true}
                                  onChange={() => toggleItemFlag(pool.pool_id, idx, "is_controllable")}
                                  className="w-3.5 h-3.5"
                                />
                              </td>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={item.is_variable ?? CATEGORY_VARIABLE_DEFAULTS[item.category] ?? false}
                                  onChange={() => toggleItemFlag(pool.pool_id, idx, "is_variable")}
                                  className="w-3.5 h-3.5"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Pool settings */}
                <div>
                  <h4 className="text-xs font-semibold uppercase text-bark-400 tracking-wider mb-3">Pool Settings</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-bark-500 mb-1">Default Recovery Method</label>
                      <select
                        value={pool.default_recovery_method}
                        onChange={(e) => updatePool(pool.pool_id, "default_recovery_method", e.target.value)}
                        className="input-sm w-full"
                      >
                        {RECOVERY_METHODS.map((m) => (
                          <option key={m} value={m}>{METHOD_LABELS[m]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bark-500 mb-1">Base Year Amount</label>
                      <input
                        type="number"
                        value={pool.default_base_year_amount ?? ""}
                        onChange={(e) => updatePool(pool.pool_id, "default_base_year_amount", e.target.value ? Number(e.target.value) : null)}
                        className="input-sm w-full"
                        placeholder="Auto"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bark-500 mb-1">Annual Ceiling %</label>
                      <PctInput
                        value={pool.default_expense_cap_pct}
                        onChange={(v) => updatePool(pool.pool_id, "default_expense_cap_pct", v)}
                        placeholder="None"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bark-500 mb-1">Annual Floor %</label>
                      <PctInput
                        value={(pool as unknown as Record<string, unknown>).default_cap_floor_pct as number | null | undefined}
                        onChange={(v) => updatePool(pool.pool_id, "default_cap_floor_pct", v)}
                        placeholder="None"
                      />
                      <p className="text-[10px] text-bark-300 mt-0.5">
                        Minimum annual CAM increase (enter 2 for 2%)
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bark-500 mb-1">Admin Fee %</label>
                      <PctInput
                        value={pool.admin_fee_pct}
                        onChange={(v) => updatePool(pool.pool_id, "admin_fee_pct", v ?? 0)}
                        placeholder="e.g. 15"
                      />
                    </div>
                    {pool.pool_type === "cam" && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-bark-500 mb-1">Mgmt Fee %</label>
                          <PctInput
                            value={pool.management_fee_pct}
                            onChange={(v) => updatePool(pool.pool_id, "management_fee_pct", v ?? 0)}
                            placeholder="e.g. 5"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-bark-500 mb-1">Mgmt Fee Cap ($)</label>
                          <input
                            type="number"
                            step="100"
                            value={(pool as unknown as Record<string, unknown>).management_fee_cap as number ?? ""}
                            onChange={(e) => updatePool(pool.pool_id, "management_fee_cap", e.target.value ? Number(e.target.value) : null)}
                            className="input-sm w-full"
                            placeholder="No cap"
                          />
                        </div>
                      </>
                    )}
                    <div className="flex items-center gap-2 pt-4">
                      <input
                        type="checkbox"
                        id={`gu_${pool.pool_id}`}
                        checked={pool.gross_up_enabled}
                        onChange={(e) => updatePool(pool.pool_id, "gross_up_enabled", e.target.checked)}
                        className="w-3.5 h-3.5"
                      />
                      <label htmlFor={`gu_${pool.pool_id}`} className="text-xs font-medium text-bark-500">
                        Gross-Up Enabled
                      </label>
                    </div>
                    {pool.gross_up_enabled && (
                      <div>
                        <label className="block text-xs font-medium text-bark-500 mb-1">Gross-Up Threshold</label>
                        <PctInput
                          value={pool.gross_up_threshold}
                          onChange={(v) => updatePool(pool.pool_id, "gross_up_threshold", v ?? 0.95)}
                          placeholder="e.g. 95"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-4">
                      <input
                        type="checkbox"
                        id={`cc_${pool.pool_id}`}
                        checked={(pool as unknown as Record<string, unknown>).cap_controllable_only as boolean ?? false}
                        onChange={(e) => updatePool(pool.pool_id, "cap_controllable_only", e.target.checked)}
                        className="w-3.5 h-3.5"
                      />
                      <label htmlFor={`cc_${pool.pool_id}`} className="text-xs font-medium text-bark-500">
                        Cap Controllable Only
                      </label>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-grove-200">
                  <div>
                    {!isStandard && (
                      <button
                        onClick={() => handleDelete(pool.pool_id)}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete Pool
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleSave(pool)}
                    disabled={saving === pool.pool_id}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-forest-700 text-white rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-colors"
                  >
                    {saved === pool.pool_id ? (
                      <><CheckCircle className="w-3.5 h-3.5" /> Saved</>
                    ) : saving === pool.pool_id ? (
                      "Saving..."
                    ) : (
                      <><Save className="w-3.5 h-3.5" /> Save Pool</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add custom pool */}
      <div className="bg-white rounded-xl border border-grove-300 p-4">
        <div className="flex items-center gap-3">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New custom pool name..."
            className="flex-1 input-sm"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-honey-600 text-white rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Pool
          </button>
        </div>
      </div>
    </div>
  );
}
